import { TransferOrderStatus } from '@nudool/shared';
import { parseListRouteSearch } from '../../../hooks/use-list-table-helpers/index.js';
import { uuidPattern } from '../../../lib/uuid-pattern.js';
import {
  TRANSFER_ORDER_WORKSPACE_QUEUES,
  type TransferOrderWorkspaceQueue,
} from '../transfer-order-workspace-queues.js';
import { compatibleStatusIds } from './compatible-status-ids.js';
import { dateFromParam } from './date-from-param.js';
import { DATE_PARAM_RE } from './date-param-pattern.js';
import type { TransferOrderWorkspaceSearch } from './transfer-order-workspace-search.js';

const KNOWN_STATUS_IDS = new Set<number>(
  Object.values(TransferOrderStatus).filter((value): value is number => typeof value === 'number'),
);

const DEFAULT_TRANSFER_ORDER_QUEUE: TransferOrderWorkspaceQueue = 'open';

function parseQueue(value: unknown): TransferOrderWorkspaceQueue {
  return TRANSFER_ORDER_WORKSPACE_QUEUES.some((item) => item.id === value)
    ? (value as TransferOrderWorkspaceQueue)
    : DEFAULT_TRANSFER_ORDER_QUEUE;
}

function parseStatusIds(value: unknown): number[] {
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : typeof value === 'number'
        ? [value]
        : [];
  const statusIds = raw
    .map((entry) => (typeof entry === 'number' ? entry : Number(String(entry).trim())))
    .filter((statusId) => KNOWN_STATUS_IDS.has(statusId));
  return [...new Set(statusIds)];
}

function parseUuid(value: unknown): string | undefined {
  return typeof value === 'string' && uuidPattern.test(value.trim()) ? value.trim() : undefined;
}

function parseDateParam(value: unknown): string | undefined {
  return typeof value === 'string' && DATE_PARAM_RE.test(value) && dateFromParam(value)
    ? value
    : undefined;
}

export function parseTransferOrderWorkspaceSearch(
  search: Record<string, unknown>,
): TransferOrderWorkspaceSearch {
  const queue = parseQueue(search.queue);
  const parsed: TransferOrderWorkspaceSearch = { queue };
  const statusIds = compatibleStatusIds(queue, parseStatusIds(search.statusIds));
  if (statusIds.length > 0) parsed.statusIds = statusIds;
  const brandId = parseUuid(search.brandId);
  if (brandId) parsed.brandId = brandId;
  const warehouseId = parseUuid(search.warehouseId);
  if (warehouseId) parsed.warehouseId = warehouseId;
  const createdFrom = parseDateParam(search.createdFrom);
  if (createdFrom) parsed.createdFrom = createdFrom;
  const createdTo = parseDateParam(search.createdTo);
  if (createdTo) parsed.createdTo = createdTo;
  return { ...parsed, ...parseListRouteSearch(search) };
}
